import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  MenuItem,
  FormControl,
  TextareaAutosize,
  Grid,
  useTheme,
  CircularProgress, // For loading state
  Alert, // For feedback messages
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import {
  ContactFormRoot,
  TextFieldContactForm,
  SelectContactForm,
  InputLabelContactForm,
} from "./ContactForm.styles";

// --- IMPORT THE BACKEND API CALL ---
import { sendConfigurationEmail } from "../../backendEmail/ParseServerClient";
import { createShareableUrl } from "../../helpers/config-utilities";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import { getData, getName, getCode } from "country-list";

const R8_VARIANTS = [
  "R8 V10 Quattro",
  "R8 V10 RWS",
  "R8 V10 Performance Quattro",
  "R8 V10 Performance RWD",
  "R8 V10 GT RWS",
  "R8 V10 Spyder Quattro",
  "R8 V10 Spyder RWS",
  "R8 V10 Performance Quattro Spyder",
  "R8 V10 Performance Spyder RWD",
];

const MAX_MODEL_YEAR = 2024;

const getAvailableYears = () => {
  const years = [];
  for (let y = 2015; y <= MAX_MODEL_YEAR; y++) {
    years.push(y);
  }

  return years.reverse(); // Show newest first
};

/**
 * Helper to format a single selected option into a readable text block for the email body.
 */
const formatOptionToText = (option) => {
  if (!option || !option.name || !option.subCategory || !option.descriptor) {
    return "";
  } // Determine the configuration key and value

  const configKey = option.groupKey || Object.keys(option.descriptor)[0];
  const configValue = option.descriptor[configKey]; // Format price

  const price =
    typeof option.price === "number" && option.price > 0
      ? `$${option.price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
      : "Included";

  return [
    `\n--- Item: ${option.name} ---`,
    `Category: ${option.subCategory}`,
    `Price: ${price}`,
    `Config Key/Value: ${configKey}=${configValue}`,
  ].join("\n");
};

export const ContactForm = ({
  onClose,
  currentConfigCode,
  showContactForm,
}) => {
  const { map } = useSelectedOptions();
  const ALL_COUNTRIES = getData().sort((a, b) => a.name.localeCompare(b.name));
  const defaultCountryName =
    getName("DE") || // for the MUI <Select>
    ALL_COUNTRIES[0]?.name ||
    "";

  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: getCode(defaultCountryName) || "DE",
    email: "",
    phone: "",
    year: getAvailableYears()[0], // Default to the latest year (is a number)
    variant: R8_VARIANTS[0], // Default to the first variant
    message: "",
  });

  // State for form interaction and feedback
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback({ type: null, message: null });

    // --- START: LOGIC TO GENERATE CONFIGURATION SUMMARY (NEW) ---
    const selectedOptions = Object.values(map || {});

    // 1. Format all selected options into a single string block
    const optionsSummaryText = selectedOptions
      .filter((option) => option.inSummary)
      .map(formatOptionToText)
      .join("\n");

    // 2. Create the complete configuration details section
    const summarySection = optionsSummaryText.trim()
      ? `\n\n--- Selected Configuration Details ---\n${optionsSummaryText}`
      : "\n\n--- Configuration Details: No customization options selected. ---";

    // 3. Compile the final message body, including the new summary section
    const messageBody = [
      `Message: ${formData?.message || "N/A"}`,
      `Shareable Configuration URL: ${createShareableUrl()}`,
      summarySection,
    ].join("\n");
    // --- END: LOGIC TO GENERATE CONFIGURATION SUMMARY (NEW) ---

    const payload = {
      name: `${formData?.firstName} ${formData?.lastName}`,
      email: formData?.email,
      carModel: formData?.variant,
      carYear: String(formData?.year),
      country: getName(formData.country) || formData.country,
      phone: formData?.phone || "N/A",
      message: messageBody,
    };

    try {
      // 2. Call the backend function
      const result = await sendConfigurationEmail(payload);

      if (result.success) {
        setFeedback({
          type: "success",
          message: "Inquiry submitted! Thanks for your interest",
        });
        // We do NOT call onClose() immediately so the user can see the success message.
      } else {
        // Handle validation or server error messages from the backend
        setFeedback({
          type: "error",
          message:
            result.error || "Failed to submit inquiry due to a server error.",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setFeedback({
        type: "error",
        message: "An unexpected error occurred during submission.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContactFormRoot component="form" onSubmit={handleSubmit}>
      <Button
        variant="text"
        sx={{
          position: "absolute",
          zIndex: 9,
          top: 0,
          right: { xs: "-10px", md: "-5px" },
          color: theme?.palette?.companyColors?.secondary,
          fontSize: { xs: "1.75rem", lg: "2.25rem" },
          transition: "all 0.3s ease",
        }}
        onClick={() => onClose(showContactForm)}
      >
        <CloseIcon
          sx={{
            padding: "3px 0 0",
            fontSize: "3.5rem",
          }}
        />
      </Button>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          flex: 1,
          fontSize: { xs: "2.25rem", lg: "2rem" },
        }}
      >
        Interested in this body kit? Fill out the form below and we’ll keep you
        updated.
      </Typography>
      <Typography
        variant="body1"
        sx={{
          width: "100%",
          fontSize: { xs: "1.6rem", lg: "1.4rem" },
        }}
      >
        The prices shown are indicative and may evolve as development
        progresses. Your submission helps us shape production volumes and bring
        this body kit to life.
      </Typography>

      {/* Feedback Messages */}
      {feedback.type && (
        <Alert severity={feedback.type} sx={{ mb: 3, fontSize: "1.5rem" }}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Name and Last Name */}
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextFieldContactForm
            fullWidth
            required
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            disabled={loading || feedback.type === "success"}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextFieldContactForm
            fullWidth
            required
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            disabled={loading || feedback.type === "success"}
          />
        </Grid>

        {/* Country and Phone */}
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormControl
            fullWidth
            required
            disabled={loading || feedback?.type === "success"}
          >
            <InputLabelContactForm>Country</InputLabelContactForm>
            <SelectContactForm
              label="Country"
              name="country"
              value={formData?.country}
              onChange={handleChange}
            >
              {ALL_COUNTRIES?.map((c) => (
                <MenuItem
                  key={c.code}
                  value={c.code}
                  sx={{ fontSize: "1.35rem" }}
                >
                  {c.name}
                </MenuItem>
              ))}
            </SelectContactForm>
          </FormControl>
        </Grid>
        <Grid
          item
          size={{ xs: 12, sm: 6 }}
          sx={{
            ".special-label": {
              fontFamily: "Onest",
              background: "none !important",
              fontSize: "1.15rem !important",
              color: "rgba(0, 0, 0, 0.6)",
              mt: "-3px",
              left: "10px !important",
            },
            input: {
              width: "100% !important",
              color: `${theme?.palette?.companyColors?.secondary} !important`,
              border: "none !important",
              borderBottom: `1px solid ${theme?.palette?.companyColors?.greyLight1} !important`,
              borderRadius: `${theme?.borderRadius?.small} !important`,
              fontSize: "1.75rem !important",
              [theme.breakpoints.up("lg")]: {
                fontSize: "1.35rem !important",
              },
            },
          }}
        >
          <FormControl
            fullWidth
            required
            disabled={loading || feedback?.type === "success"}
            sx={{ opacity: loading || feedback?.type === "success" ? 0.5 : 1 }}
          >
            <PhoneInput
              country={formData?.country?.toLowerCase()}
              required
              value={formData?.phone}
              onChange={(_, __, ___, formattedValue) => {
                const eventObj = {
                  target: {
                    name: "phone",
                    value: formattedValue,
                  },
                };
                handleChange(eventObj);
              }}
              placeholder="Phone number"
              disabled={loading || feedback.type === "success"}
              inputProps={{
                name: "phone",
                required: true,
                autoFocus: true,
              }}
            />
          </FormControl>
        </Grid>

        {/* Email */}
        <Grid item size={{ xs: 12 }}>
          <TextFieldContactForm
            fullWidth
            required
            type="email"
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading || feedback.type === "success"}
          />
        </Grid>

        {/* Year and Variant Selects */}
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormControl
            fullWidth
            required
            disabled={loading || feedback?.type === "success"}
          >
            <InputLabelContactForm>
              Your Audi R8 Model Year
            </InputLabelContactForm>
            <SelectContactForm
              label="Preferred Model Year"
              name="year"
              value={formData?.year}
              onChange={handleChange}
            >
              {getAvailableYears().map((year) => (
                <MenuItem
                  key={year}
                  value={year}
                  sx={{
                    fontSize: "1.75rem",
                    [theme.breakpoints.up("lg")]: {
                      fontSize: "1.35rem",
                    },
                  }}
                >
                  {year}
                </MenuItem>
              ))}
            </SelectContactForm>
          </FormControl>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormControl
            fullWidth
            required
            disabled={loading || feedback?.type === "success"}
          >
            <InputLabelContactForm>Variant</InputLabelContactForm>
            <SelectContactForm
              label="Variant of Interest"
              name="variant"
              value={formData?.variant}
              onChange={handleChange}
            >
              {R8_VARIANTS.map((variant) => (
                <MenuItem
                  key={variant}
                  value={variant}
                  sx={{
                    fontSize: "1.75rem",
                    [theme.breakpoints.up("lg")]: {
                      fontSize: "1.35rem",
                    },
                  }}
                >
                  {variant}
                </MenuItem>
              ))}
            </SelectContactForm>
          </FormControl>
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={loading || feedback.type === "success"}
            sx={{
              "& .MuiInputBase-root": {
                padding: 0,
              },
            }}
          >
            <InputLabelContactForm shrink htmlFor="personal-message-textarea">
              Message (Optional)
            </InputLabelContactForm>

            <Box sx={{ position: "relative", pt: 1.5 }}>
              <TextareaAutosize
                id="personal-message-textarea"
                name="message"
                minRows={4}
                maxRows={15}
                placeholder="Include any specific questions or details about your configuration."
                value={formData?.message}
                onChange={handleChange}
                style={{
                  width: "100%",
                  resize: "none", // Prevent manual resizing by user
                  padding: "16.5px 14px",
                  fontFamily: "Onest",
                  lineHeight: 1.5,
                  color: theme?.palette?.companyColors?.secondary,
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                  backgroundColor: theme?.palette?.companyColors?.neutral,
                  borderRadius: theme?.borderRadius?.small,
                  border: "none",
                  borderBottom: `1px solid ${theme?.palette?.companyColors?.greyLight1}`,
                  fontSize: "1.75rem",
                  [theme.breakpoints.up("lg")]: {
                    fontSize: "1.35rem",
                  },
                  "&:focus": {
                    borderColor: theme?.palette?.primary?.greyLight1,
                    outline: "none",
                  },
                  // Ensure compatibility with disabled state
                  ...(loading || feedback.type === "success"
                    ? {
                        opacity: 0.6,
                        cursor: "not-allowed",
                        backgroundColor:
                          theme?.palette?.action?.disabledBackground,
                      }
                    : {}),
                }}
                disabled={loading || feedback.type === "success"}
              />
            </Box>
          </FormControl>
        </Grid>
      </Grid>

      <Button
        type="submit"
        variant="contained"
        endIcon={
          loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon />
          )
        }
        sx={{
          color: theme?.palette?.companyColors?.neutral,
          background: theme?.palette?.companyColors?.primary,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: theme?.borderRadius?.small,
          padding: { xs: "1rem 5rem", xl: "1rem 4rem" },
          width: "100%",
          mt: "3rem",
          boxShadow: "none",
        }}
        fullWidth
        disabled={loading || feedback.type === "success"}
      >
        <Typography
          variant="button"
          sx={{
            textTransform: "initial",
            fontSize: { xs: "1.85rem", lg: "1.75rem" },
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          {loading
            ? "Sending..."
            : feedback.type === "success"
            ? "Inquiry Sent!"
            : "Submit Inquiry"}
        </Typography>
      </Button>
    </ContactFormRoot>
  );
};
